import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaHistory, FaArrowLeft, FaComment, FaFileUpload, FaCheckCircle, FaFilePdf } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

export default function UserHistory() {
  const { t, i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const { userId } = useParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const [userRes, historyRes] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/history/user/${userId}`)
      ]);
      setUser(userRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message': return <FaComment className="text-blue-500" />;
      case 'file': return <FaFileUpload className="text-[#00D4FF]" />;
      case 'validation': return <FaCheckCircle className="text-[#635BFF]" />;
      case 'pdf': return <FaFilePdf className="text-red-500" />;
      default: return <FaHistory className="text-gray-500" />;
    }
  };

  const formatDate = (iso) => {
    return new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) return <div className="p-8 text-center">Chargement...</div>;

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5] p-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/users')}
            className="mb-4"
          >
            <FaArrowLeft className="mr-2" /> 
            {i18n.language === 'fr' ? 'Retour aux utilisateurs' : 'Back to users'}
          </Button>

          <Card>
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-3">
                <FaHistory />
                <div>
                  {i18n.language === 'fr' ? 'Historique complet' : 'Full History'} : 
                  <span className="text-[#0A2540] ml-2">{user?.name}</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {history.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {i18n.language === 'fr' ? 'Aucun historique disponible' : 'No history available'}
                  </div>
                ) : (
                  history.map((item) => (
                    <div key={item.id} className="p-4 hover:bg-gray-50 flex gap-4">
                      <div className="mt-1 text-xl">
                        {getIcon(item.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {formatDate(item.date)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {item.description}
                        </p>
                        {item.type === 'pdf' && item.metadata?.download_url && (
                          <div className="mt-2">
                            <span className="text-xs text-red-500 font-medium">
                              PDF généré
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
